import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import StopCard from './StopCard';
import PostFinishSheet from './PostFinishSheet';
import RouteMapView from './RouteMapView';
import TravelRail from '../../partial/TravelRail';
import openNavigation from '../../../util/openNavigation';
import RouteService from '../../../services/routeService';
import PaymentService from '../../../services/paymentService';
import serviceItemsService from '../../../services/serviceItemsService';
import ScopeService from '../../../services/scopeService';
import ItemMaterialCaptureModal from '../../partial/ItemMaterialCaptureModal';
import ItemCompletionGuardModal from '../../partial/ItemCompletionGuardModal';
import { HTTPService, generateIdempotencyKey } from '../../../services/httpService';
import { getCurrentPosition, isWithinGeofence, GeolocationError } from '../../../util/geoUtils';
import { formatHeader } from '../../../util/dayStatSummary';
import { STOP_DISPLAY_STATES, deriveStopDisplayState } from '../../../util/routeStopState';
import { isTechnician } from '../../../util/permissions';

const API = new HTTPService();

// Default geofence radius for auto-arrival, in meters. Generous to account
// for parking-lot offsets and address geocoding precision.
const ARRIVED_RADIUS_METERS = 125;

// localStorage keys
const EXPLAINER_KEY = (userId) => `mobileRouteExplainerSeen:${userId}`;

// Mode toggle (list vs map) is owned by MyWork as part of the 3-way
// List/Map/Reminders control. RouteView just renders whatever mode the
// parent asks for via the `mode` prop. Default to 'list' if not provided.
class RouteView extends Component {
  state = {
    route: null,
    loading: true,
    error: null,
    showExplainer: false,
    locationPermission: 'unknown', // 'unknown' | 'granted' | 'denied' | 'unavailable'
    showPostFinish: false,
    justFinishedStop: null,
    // Invoice generated immediately after the last service of a stop finishes.
    // Mirrors the auto-fire of POST /invoices/ inside handleCompleteEstimate
    // on the web (requestimates.jsx:2879). Passed to PostFinishSheet which
    // surfaces the retail collection screen or the dealer confirmation banner.
    justFinishedInvoice: null,
    // Exclusive expansion: only one stop card open at a time. Holds the
    // request_service_id of the currently-expanded card (or null).
    expandedStopId: null,
    // Per-item material capture (mirrors shop-tech finish flow). When the
    // tech taps Complete Service and the stop has incomplete material items
    // that require data collection, we hold the stop + items here and open
    // ItemMaterialCaptureModal. On modal completion we fire finished_at.
    showItemCaptureModal: false,
    itemCaptureStop: null,
    itemCaptureService: null,
    itemCaptureItems: [],
    categoryConfigs: {},
    // Server-driven task gate. When Complete Service hits the server's
    // task-completion check and returns 400 with task_status, hold the stop
    // and surface ItemCompletionGuardModal. User can "Force Complete Anyway"
    // which retries with force_complete: true.
    showGuardModal: false,
    guardStop: null,
    guardServiceName: '',
    guardTaskStatus: null,
  };

  async componentDidMount() {
    const userId = this.props.user?.User_id;
    const seen = userId ? localStorage.getItem(EXPLAINER_KEY(userId)) : '1';
    if (!seen) this.setState({ showExplainer: true });

    this.loadCategoryConfigs();
    await this.loadRoute();
    // Auto-arrival probe on initial mount — if tech opened the app at the
    // active stop, fire arrived_at without waiting for them to tap anything.
    this.maybeAutoArrive();
  }

  async componentDidUpdate(prevProps) {
    // Date ribbon in MyWork can switch the day under us; reload the route when
    // the parent's selectedDate prop changes.
    if (prevProps.selectedDate !== this.props.selectedDate) {
      await this.loadRoute();
    }
  }

  loadCategoryConfigs = async () => {
    try {
      const { data: configs } = await API.get('/serviceCategoryConfig');
      const map = {};
      if (Array.isArray(configs)) configs.forEach(c => { map[c.category_name] = c; });
      this.setState({ categoryConfigs: map });
    } catch (err) {
      // Silent — material capture modal will operate without per-category config.
    }
  };

  loadRoute = async () => {
    const { currentLocation, selectedDate } = this.props;
    if (!currentLocation?.location_id) {
      this.setState({ loading: false, error: 'No location selected.' });
      return;
    }
    this.setState({ loading: true, error: null });
    try {
      const date = selectedDate || moment().format('YYYY-MM-DD');
      const route = await RouteService.getRoute(currentLocation.location_id, date);
      this.setState({ route, loading: false });
    } catch (err) {
      // Server endpoint may not be live yet — degrade gracefully.
      const status = err?.response?.status;
      const reason = status === 404
        ? 'Route endpoint not yet available — server is building this.'
        : (err?.message || 'Could not load route.');
      this.setState({ loading: false, error: reason });
    }
  };

  maybeAutoArrive = async () => {
    const { route } = this.state;
    if (!route?.stops) return;
    const activeStop = route.stops.find(s => {
      const ds = deriveStopDisplayState(s.state);
      return ds === STOP_DISPLAY_STATES.IN_TRAVEL || ds === STOP_DISPLAY_STATES.UP_NEXT;
    });
    if (!activeStop) return;
    const lat = activeStop.customer?.lat;
    const lng = activeStop.customer?.lng;
    if (lat == null || lng == null) return;

    try {
      const position = await getCurrentPosition({ enableHighAccuracy: true, timeout: 8000, maximumAge: 30000 });
      this.setState({ locationPermission: 'granted' });
      const here = { lat: position.coords.latitude, lng: position.coords.longitude };
      if (isWithinGeofence(here, { lat, lng }, ARRIVED_RADIUS_METERS)) {
        await RouteService.recordArrival(activeStop.request_service_id, {
          idempotencyKey: generateIdempotencyKey(),
          position: { lat: here.lat, lng: here.lng, accuracy: position.coords.accuracy },
          occurredAt: new Date().toISOString(),
        });
        toast.success(`Arrived at ${activeStop.customer?.name || 'stop'}`, { autoClose: 2000 });
        this.loadRoute();
      }
    } catch (err) {
      if (err instanceof GeolocationError) {
        this.setState({
          locationPermission: err.code === 'PERMISSION_DENIED' ? 'denied' : 'unavailable',
        });
      }
      // Silent failure — manual "I've arrived" chip stays available on the card.
    }
  };

  handleDismissExplainer = () => {
    const userId = this.props.user?.User_id;
    if (userId) localStorage.setItem(EXPLAINER_KEY(userId), '1');
    this.setState({ showExplainer: false });
    // Now that the explainer is dismissed, probe location to trigger the
    // browser permission prompt (if not already answered).
    this.maybeAutoArrive();
  };

  handleArrivedManually = async (stop) => {
    try {
      await RouteService.recordArrival(stop.request_service_id, {
        idempotencyKey: generateIdempotencyKey(),
        occurredAt: new Date().toISOString(),
      });
      toast.success('Arrival recorded', { autoClose: 1500 });
      this.loadRoute();
    } catch (err) {
      toast.error('Could not record arrival. Try again.');
    }
  };

  // "Start" button on a route stop opens the existing CheckInWizard. The
  // wizard captures VIN, photos, odometer, and signatures per the stop's
  // per-category data_collect flags. Request-service transitions (arrived_at
  // + started_at) are NOT fired here — they fire only inside the wizard's
  // completeCheckin path so that cancelling the wizard leaves the stop in
  // its prior state, and successful check-in jumps the stop straight to
  // in_progress (skipping the intermediate "Start Service" UI).
  handleCheckIn = (stop) => {
    if (!stop?.request_id) return;
    this.props.history.push(`/requests/${stop.request_id}/checkin`);
  };

  handleStartTravel = async (stop) => {
    try {
      await RouteService.startTravel(stop.request_service_id, {
        idempotencyKey: generateIdempotencyKey(),
        occurredAt: new Date().toISOString(),
      });
      this.loadRoute();
    } catch (err) {
      toast.error('Could not start travel.');
    }
  };

  handleStartService = async (stop) => {
    try {
      await RouteService.startService(stop.request_service_id, {
        idempotencyKey: generateIdempotencyKey(),
        occurredAt: new Date().toISOString(),
      });
      this.loadRoute();
    } catch (err) {
      toast.error('Could not start service.');
    }
  };

  handleFinishStop = async (stop) => {
    // Mirror shop-tech finish behavior: if any service on this stop has
    // incomplete material tasks AND requires data collection, open the
    // per-item capture modal first. On capture-modal completion we fall
    // through and fire finished_at.
    const companyData = ScopeService.getCompany() || {};
    const materialsTrackingEnabled = companyData?.materials_tracking_enabled !== false;
    const services = Array.isArray(stop.services) ? stop.services : [];
    const captureItems = [];
    let captureService = null;
    services.forEach(svc => {
      if (!svc.data_collect_required) return;
      const items = Array.isArray(svc.items) ? svc.items : [];
      const incompleteMats = items.filter(it => (!it.type || it.type === 'material') && it.status !== 'completed' && it.status !== 'skipped');
      if (incompleteMats.length > 0) {
        captureItems.push(...incompleteMats);
        if (!captureService) captureService = svc;
      }
    });
    if (materialsTrackingEnabled && captureItems.length > 0 && captureService) {
      this.setState({
        showItemCaptureModal: true,
        itemCaptureStop: stop,
        itemCaptureService: captureService,
        itemCaptureItems: captureItems,
      });
      return;
    }
    return this.commitFinishStop(stop);
  };

  commitFinishStop = async (stop, { force = false } = {}) => {
    // STEP 1 — Atomic request completion (only on the LAST service of a request).
    // POST /requests/:id/complete fires all four state updates (request,
    // request_offered, installer_calendar, request_state) inside one MySQL
    // transaction, then attempts invoice creation as a non-rolled-back follow-on.
    //
    //   - State-transaction failure → server rolls back → we bail; stop stays
    //     in_progress and tech can retry.
    //   - Invoice failure (state already committed) → response still success
    //     with invoiceId: null + invoiceError; surface a toast warning and
    //     continue. Admin can retry just the invoice from financials.
    //
    // For multi-service requests, this fires only on the LAST service (Ceramic
    // + Tint as one job spread across two scheduled slots — the request closes
    // out when the second one finishes).
    let invoice = null;
    const siblings = (this.state.route?.stops || []).filter(
      s => s.request_id === stop.request_id && s.request_service_id !== stop.request_service_id
    );
    const isLastServiceOnRequest = siblings.every(s => !!s.state?.finished_at);
    if (isLastServiceOnRequest) {
      let result;
      try {
        result = await PaymentService.completeRequest({
          requestId: stop.request_id,
          installerId: this.props.typeId,
          locationId: this.props.currentLocation?.location_id || null,
          userId: this.props.user?.User_id,
          userType: this.props.user?.User_type || 'installer',
          createInvoice: true,
        });
      } catch (completeErr) {
        // State transaction rolled back — request stays in_progress. Tech retries.
        console.error('completeRequest failed; stop NOT marked completed:', completeErr?.message || completeErr);
        toast.error(`Could not complete request. ${completeErr?.message || 'Retry or contact support.'}`);
        return;
      }

      invoice = result.invoice;
      if (result.invoiceError) {
        // State committed; invoice creation failed (e.g. Stripe-side).
        // PostFinishSheet's "Invoice didn't generate" soft warning surfaces
        // this state to the tech; admin retries invoice from financials.
        console.warn('Request completed but invoice creation failed:', result.invoiceError);
        toast.warn(`Request marked complete — invoice creation deferred. ${result.invoiceError}`);
      }
    }

    // STEP 2 — Fire finished_at on the request_service so the stop UI flips.
    try {
      await RouteService.finishService(stop.request_service_id, {
        idempotencyKey: generateIdempotencyKey(),
        occurredAt: new Date().toISOString(),
        forceComplete: force,
      });

      this.setState({
        justFinishedStop: stop,
        justFinishedInvoice: invoice,
        showPostFinish: true,
      });
      this.loadRoute();
    } catch (err) {
      // Server gate: incomplete request_service_tasks. Surface the guard
      // modal so the tech sees what's missing and can force-complete.
      const status = err?.response?.status;
      const body = err?.response?.data;
      if (status === 400 && body?.task_status) {
        const firstSvc = Array.isArray(stop.services) && stop.services.length > 0 ? stop.services[0] : null;
        const svcLabel = firstSvc
          ? `${firstSvc.service_category || ''}${firstSvc.custom_description ? ' - ' + firstSvc.custom_description : ''}`.trim()
          : 'this stop';
        this.setState({
          showGuardModal: true,
          guardStop: stop,
          guardServiceName: svcLabel || 'this stop',
          guardTaskStatus: body.task_status,
        });
        return;
      }
      toast.error('Could not finish stop.');
    }
  };

  handleGuardForceComplete = () => {
    const stop = this.state.guardStop;
    this.setState({ showGuardModal: false, guardStop: null, guardServiceName: '', guardTaskStatus: null });
    if (stop) this.commitFinishStop(stop, { force: true });
  };

  handleGuardCancel = () => {
    this.setState({ showGuardModal: false, guardStop: null, guardServiceName: '', guardTaskStatus: null });
  };

  handleItemCaptureComplete = () => {
    const stop = this.state.itemCaptureStop;
    this.setState({
      showItemCaptureModal: false,
      itemCaptureStop: null,
      itemCaptureService: null,
      itemCaptureItems: [],
    });
    if (stop) this.commitFinishStop(stop);
  };

  handleItemCaptureCancel = () => {
    this.setState({
      showItemCaptureModal: false,
      itemCaptureStop: null,
      itemCaptureService: null,
      itemCaptureItems: [],
    });
  };

  // "Directions to base" — fetch the device's current GPS fix so the maps
  // app opens with FROM = current location and TO = base address pre-filled.
  // Fallback: if geolocation is unavailable or times out, open with just
  // the destination — the maps app's own "current location" still works.
  handleNavigateToBase = async (baseAddress) => {
    try {
      const position = await getCurrentPosition({
        enableHighAccuracy: false,
        timeout: 4000,
        maximumAge: 60000,
      });
      openNavigation(baseAddress, {
        origin: { lat: position.lat, lng: position.lng },
      });
    } catch (err) {
      // Permission denied / timeout / unavailable — open with destination
      // only; the maps app will use its own current-location notion.
      openNavigation(baseAddress);
    }
  };

  handleNavigateNext = async (nextStop) => {
    try {
      await RouteService.startTravel(nextStop.request_service_id, {
        idempotencyKey: generateIdempotencyKey(),
        occurredAt: new Date().toISOString(),
      });
    } catch (err) {
      // Don't block nav app from opening — log silently.
      console.warn('Could not record started_travel_at:', err.message);
    }
    // Nav-app handoff happens inside PostFinishSheet via openNavigation.
  };

  handleToggleExpand = (stop) => {
    const stopId = stop?.request_service_id;
    if (!stopId) return;
    this.setState(prev => ({
      expandedStopId: prev.expandedStopId === stopId ? null : stopId,
    }));
  };

  // Item toggle for work-step checkboxes inside the expanded StopCard.
  // Simpler than MyWork's variant: route stops always carry job-level items
  // (no template inflation here — that happens at booking time). After a
  // successful toggle we reload the route so the checked state and any
  // downstream service-state changes (auto-finish) reflect in the UI.
  handleToggleItem = async (item) => {
    if (!item || !item.id) return;
    try {
      await serviceItemsService.completeItemWithMaterials(item.id);
      await this.loadRoute();
    } catch (err) {
      toast.error('Could not update step.');
    }
  };

  findNextStop = (justFinished) => {
    const { route } = this.state;
    if (!justFinished || !route?.stops) return null;
    const idx = route.stops.findIndex(s => s.request_service_id === justFinished.request_service_id);
    if (idx === -1 || idx === route.stops.length - 1) return null;
    return route.stops[idx + 1];
  };

  render() {
    const { route, loading, error, showExplainer, showPostFinish, justFinishedStop, locationPermission, expandedStopId } = this.state;
    const mode = this.props.mode === 'map' ? 'map' : 'list';
    const role = (this.props.user?.role || '').toLowerCase();
    const userId = this.props.user?.User_id;
    // Role-based stop scoping (set with server team 2026-05-14):
    //   technician → only stops assigned to me
    //   owner/admin/manager/viewer → every stop at the location (dispatch)
    // Server returns all location stops; filter is client-side.
    const allStops = route?.stops || [];
    const visibleStops = isTechnician(role)
      ? allStops.filter(s => String(s.assigned_technician_id) === String(userId))
      : allStops;
    // Map coloring: techs see state-based colors (their own stops only).
    // Non-techs see per-tech colors so the dispatch view differentiates crews.
    const useDispatchColors = !isTechnician(role);

    if (loading) {
      return (
        <div className="text-center py-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading…</span>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="container py-4">
          <div className="alert alert-warning">
            <i className="fa-sharp fa-regular fa-triangle-exclamation mr-2" />
            {error}
            <button className="btn btn-link btn-sm" onClick={this.loadRoute}>Retry</button>
          </div>
        </div>
      );
    }

    const stops = visibleStops;
    const headerText = formatHeader(route?.day_summary, stops.length);
    const firstStop = stops[0];
    const nextStop = this.findNextStop(justFinishedStop);

    return (
      <div className="route-view container py-3" style={{ maxWidth: 640 }}>
        {/* Header */}
        <div className="d-flex justify-content-between align-items-baseline mb-3">
          <div>
            <h4 className="mb-1">
              {(() => {
                const d = moment(this.props.selectedDate || undefined);
                const today = moment().startOf('day');
                const tomorrow = moment().add(1, 'day').startOf('day');
                const yesterday = moment().subtract(1, 'day').startOf('day');
                if (d.isSame(today, 'day')) return `Today, ${d.format('ddd MMM D')}`;
                if (d.isSame(tomorrow, 'day')) return `Tomorrow, ${d.format('ddd MMM D')}`;
                if (d.isSame(yesterday, 'day')) return `Yesterday, ${d.format('ddd MMM D')}`;
                return d.format('dddd, MMM D');
              })()}
            </h4>
            {headerText && <small className="text-muted">{headerText}</small>}
          </div>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={this.loadRoute} title="Refresh route">
            <i className="fa-sharp fa-regular fa-arrow-rotate-right" aria-hidden="true" />
          </button>
        </div>

        {/* Permission denied banner — soft, non-blocking */}
        {locationPermission === 'denied' && (
          <div className="alert alert-light border" style={{ fontSize: 13 }}>
            <i className="fa-sharp fa-regular fa-location-dot-slash mr-2" />
            Location is off. Arrivals won't auto-confirm — use the "I've arrived" chip on each stop.
          </div>
        )}

        {mode === 'map' ? (
          <RouteMapView
            stops={stops}
            currentLocation={this.props.currentLocation}
            useDispatchColors={useDispatchColors}
            expandedStopId={expandedStopId}
            onSelectStop={this.handleToggleExpand}
            onStartTravel={this.handleStartTravel}
            onCheckIn={this.handleCheckIn}
            onStartService={this.handleStartService}
            onFinishStop={this.handleFinishStop}
            onToggleItem={this.handleToggleItem}
            currentUserId={userId}
          />
        ) : (
          <>
            {/* Depart base header — only when the day has stops */}
            {firstStop && firstStop.travel_minutes_in != null && (
              <div className="text-muted mb-3" style={{ fontSize: 13 }}>
                <i className="fa-sharp fa-regular fa-house mr-2" />
                Depart base · {Math.round(firstStop.travel_minutes_in)} min to first stop
              </div>
            )}

            {/* Empty state */}
            {stops.length === 0 && (
              <div className="text-center text-muted py-5">
                <i className="fa-sharp fa-regular fa-calendar-day" style={{ fontSize: 32 }} />
                <p className="mt-3">No stops scheduled for today.</p>
              </div>
            )}

            {/* Sequenced stops with travel rails between */}
            {stops.map((stop, idx) => (
              <React.Fragment key={stop.request_service_id}>
                {idx > 0 && (
                  <TravelRail
                    travelMinutes={stop.travel_minutes_in}
                    travelMiles={stop.travel_distance_miles}
                    arriveAt={stop.start_time}
                    sequence={stop.sequence}
                  />
                )}
                <StopCard
                  stop={stop}
                  expanded={this.state.expandedStopId === stop.request_service_id}
                  onToggleExpand={this.handleToggleExpand}
                  onStartTravel={this.handleStartTravel}
                  onCheckIn={this.handleCheckIn}
                  onStartService={this.handleStartService}
                  onFinishStop={this.handleFinishStop}
                  onToggleItem={this.handleToggleItem}
                  currentUserId={this.props.user?.User_id}
                />
              </React.Fragment>
            ))}

            {/* End-of-day rail + Directions to base affordance. Visible only
                in list mode, after the last stop, so the tech can hand off to
                their nav app for the drive home without backtracking. The
                button fetches the device's current GPS fix and passes BOTH
                origin (current position) and destination (base) to the nav
                app so it opens with the full route ready. */}
            {stops.length > 0 && (
              <div className="text-center text-muted mt-4" style={{ fontSize: 13 }}>
                <i className="fa-sharp fa-regular fa-flag-checkered mr-2" />
                End of day
                {(() => {
                  const loc = this.props.currentLocation;
                  const hasBaseCoords = loc?.base_lat != null && loc?.base_lng != null;
                  const addressLine = [loc?.address_line1, loc?.address_city, loc?.address_state]
                    .filter(Boolean).join(', ');
                  if (!hasBaseCoords && !addressLine) return null;
                  const baseAddress = {
                    address: addressLine,
                    lat: hasBaseCoords ? Number(loc.base_lat) : null,
                    lng: hasBaseCoords ? Number(loc.base_lng) : null,
                  };
                  return (
                    <div className="mt-2">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => this.handleNavigateToBase(baseAddress)}
                      >
                        <i className="fa-sharp fa-regular fa-house mr-2" aria-hidden="true" />
                        Directions to base
                      </button>
                    </div>
                  );
                })()}
              </div>
            )}
          </>
        )}

        {/* Permission explainer modal (first-time) */}
        {showExplainer && (
          <div
            className="modal show d-block"
            tabIndex="-1"
            style={{ background: 'rgba(0,0,0,0.5)' }}
            onClick={this.handleDismissExplainer}
          >
            <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
              <div className="modal-content">
                <div className="modal-body text-center py-4">
                  <i className="fa-sharp fa-regular fa-location-dot" style={{ fontSize: 36, color: '#3B82F6' }} />
                  <h4 className="mt-3">Let your route help you out</h4>
                  <p className="text-muted">
                    With location turned on, your arrival at each stop is confirmed automatically —
                    no manual taps. Tap the buttons on each card any time you want to override.
                  </p>
                  <button type="button" className="btn btn-primary btn-block" onClick={this.handleDismissExplainer}>
                    Got it
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Post-finish sheet */}
        <PostFinishSheet
          show={showPostFinish}
          onHide={() => this.setState({ showPostFinish: false, justFinishedStop: null, justFinishedInvoice: null })}
          nextStop={nextStop}
          baseAddress={(() => {
            const loc = this.props.currentLocation;
            if (!loc) return null;
            if (loc.base_lat == null || loc.base_lng == null) return null;
            return {
              address: [loc.address_line1, loc.address_city, loc.address_state].filter(Boolean).join(', '),
              lat: Number(loc.base_lat),
              lng: Number(loc.base_lng),
            };
          })()}
          onNavigateNext={this.handleNavigateNext}
          invoice={this.state.justFinishedInvoice}
          requestType={
            justFinishedStop?.requestType
            || justFinishedStop?.request_type
            || 'retail'
          }
          requestId={justFinishedStop?.request_id}
          installerId={this.props.typeId}
          customerName={justFinishedStop?.customer?.name}
          vehicleLabel={justFinishedStop?.vehicle_label}
        />

        {/* Per-Item Material Capture — fires before finished_at when the
            stop's services have data_collect_required items pending. Reuses
            the same modal the shop-tech ServiceCard uses on finish. */}
        {this.state.showItemCaptureModal && this.state.itemCaptureService && (
          <ItemMaterialCaptureModal
            show={this.state.showItemCaptureModal}
            onHide={this.handleItemCaptureCancel}
            service={this.state.itemCaptureService}
            items={this.state.itemCaptureItems}
            categoryConfig={this.state.categoryConfigs[this.state.itemCaptureService.service_category]}
            installerId={this.props.typeId}
            locationId={this.props.currentLocation?.location_id}
            onComplete={this.handleItemCaptureComplete}
          />
        )}

        {/* Server-driven task gate. Surfaced when Complete Service returns
            400 with task_status. User can Force Complete or Go Back. */}
        <ItemCompletionGuardModal
          show={this.state.showGuardModal}
          onHide={this.handleGuardCancel}
          serviceName={this.state.guardServiceName}
          taskStatus={this.state.guardTaskStatus}
          onForceComplete={this.handleGuardForceComplete}
        />
      </div>
    );
  }
}

export default withRouter(RouteView);
